import React, { useEffect, useState, useRef } from 'react';
import { View, Calendar, dateFnsLocalizer, SlotInfo } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { ptBR } from 'date-fns/locale/pt-BR';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './Calendario.css';
import ModalCriarAtividade from './modals/ModalCriarAtividade';
import { CronogramaViewModel } from '../../../viewmodel/CronogramaViewModel';
import { Atividade } from '../../../model/processo/Atividade';
import ModalEditarAtividade from './modals/ModalEditarAtividade';
import { EventoCalendario } from './EventoCalendario';
import Processo from '../../../model/processo/Processo';
import { ProcessosAtividadesDTO } from '../../../viewmodel/CronogramaViewModel';
import { atribuirCoresQuentes } from './utils/atribuirCores';
import { converterParaEventosCalendario } from './utils/converterEventoCalendario';
import { converterProcessoParaEventosCalendario } from './utils/converterAtividadesUsuario';
import { toast } from 'react-toastify';
import LegendaProcessos from './utils/LegendaProcessos';
import Tips from './utils/dicas';
import { useProcessoContext } from '../../../Routes/context/ProcessoUsuarioContext';
import { Button } from 'react-bootstrap';
import ModalConclusao from './modals/ModalConclusao';
import { FaClosedCaptioning, FaTimesCircle } from 'react-icons/fa';
import PermissaoWrapper from '../../../components/PermissaoWrapper';
import { PermissaoEnum } from '../../../model/Usuario/Permissao';

interface CalendarioProps {
    processoUsuario: Processo;
    zoomLevel: number;
    setZoomLevel: React.Dispatch<React.SetStateAction<number>>;
}

const locales = { 'pt-BR': ptBR };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

const Calendario: React.FC<CalendarioProps> = ({ zoomLevel, setZoomLevel }) => {
    const { processo } = useProcessoContext();
    const [showTips, setShowTips] = useState(true);
    const [atividadesUsuario, setAtividadesUsuario] = useState<EventoCalendario[]>([]);
    const [atividadesOutrasDTO, setAtividadesOutrasDTO] = useState<ProcessosAtividadesDTO[]>([]);
    const [atividadesOutras, setAtividadesOutras] = useState<EventoCalendario[]>([]);
    const calendarRef = useRef<HTMLDivElement>(null);
    const [calendarHeight, setCalendarHeight] = useState('150px');
    const [view, setView] = useState<View>('month');
    const [showModal, setShowModal] = useState(false);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [eventoSelecionado, setEventoSelecionado] = useState<EventoCalendario | null>(null);
    const [legendasVisiveis, setLegendasVisiveis] = useState(true);
    const azulUsuario = '#054f77';

    const [showModalConclusao, setShowModalConclusao] = useState(false);

    const handleShowConclusao = () => setShowModalConclusao(true);
    const handleCloseConclusao = () => setShowModalConclusao(false);

    useEffect(() => {
        CronogramaViewModel.obterCronogramasAtivos(processo?.id)
            .then(cronogramasAtivoDTO => {
                const atividadesComCores = atribuirCoresQuentes(cronogramasAtivoDTO);
                setAtividadesOutrasDTO(atividadesComCores);
                setAtividadesOutras(converterParaEventosCalendario(atividadesComCores));
            })
            .catch(error => {
                console.error('Erro ao obter atividades de outros projetos:', error);
                toast.error('Erro ao obter atividades de outros projetos');
            });

        if (processo?.cronograma?.atividades) {
            const novasAtividades = converterProcessoParaEventosCalendario(processo, azulUsuario);
            setAtividadesUsuario(novasAtividades);
        }
    }, [processo, azulUsuario]);

    useEffect(() => {
        flickCalendar()
    }, []);

    const flickCalendar = () => {
        /* Esta função serve apenas para impedir a visualização das Atividades como lista, do comportamento normal do componente */
        setView('day');
        const timer = setTimeout(() => {
            setView('month');
        }, 100);
        return () => clearTimeout(timer);
    }

    useEffect(() => {
        const updateCalendarSize = () => {
            if (calendarRef.current) {
                const menuWidth = 90;
                const menuHeight = 10;
                const width = Math.min(window.innerWidth - menuWidth, window.innerWidth * (zoomLevel / 100));
                const height = (width / 4) * 4;
                setCalendarHeight(`${height}px`);
                calendarRef.current.style.width = `${width}px`;

                const container = calendarRef.current.parentElement;
                if (container) {
                    container.style.width = `${width}px`;
                    container.style.height = `${height}px`;
                    container.style.paddingRight = '0';
                    container.style.marginTop = `${menuHeight}px`;
                }
            }
        };
        updateCalendarSize();
        window.addEventListener('resize', updateCalendarSize);
        return () => window.removeEventListener('resize', updateCalendarSize);
    }, [zoomLevel]);

    const handleTipsClose = () => {
        setShowTips(false);
    };

    const handleSelectSlot = (slotInfo: SlotInfo) => {
        // Validação para evitar erro com slots undefined
        if (!slotInfo || !slotInfo.start) {
            return;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const selectedDate = new Date(slotInfo.start);
        selectedDate.setHours(0, 0, 0, 0);

        if (selectedDate > today) {
            setSelectedDate(slotInfo.start);
            setShowModal(true);
            setEventoSelecionado(null);
        } else {
            toast.error("Data inválida, selecione data posterior à atual.");
        }
    };

    const handleSelectEvent = (evento: EventoCalendario) => {
        if (atividadesUsuario.some(e => e.id === evento.id)) {
            setEventoSelecionado(evento);
            setShowModal(true);
        }
    };

    const handleCreateAtividade = (novaAtividade: Atividade) => {
        const novoEvento: EventoCalendario = {
            id: novaAtividade.id,
            title: novaAtividade.descricao,
            start: new Date(`${novaAtividade.data}T${novaAtividade.hora}`),
            end: new Date(`${novaAtividade.data}T${novaAtividade.hora}`),
            color: azulUsuario,
            descricao: novaAtividade.descricao,
            unidade: novaAtividade.unidade,
            lugar: novaAtividade.lugar,
            data: novaAtividade.data,
            hora: novaAtividade.hora,
            cronogramaId: novaAtividade.cronogramaId,
        };

        setAtividadesUsuario((prevAtividades) => [...prevAtividades, novoEvento]);
        setShowModal(false);
        flickCalendar()
        toast.success('Atividade criada com sucesso!');
    };

    const handleEditAtividade = async (atividadeEditada: EventoCalendario) => {
        try {
            const atividade: Atividade = {
                id: atividadeEditada.id,
                hora: atividadeEditada.hora,
                data: atividadeEditada.data,
                cronogramaId: atividadeEditada.cronogramaId,
                unidade: atividadeEditada.unidade,
                descricao: atividadeEditada.descricao,
                lugar: atividadeEditada.lugar,
            };

            const atividadeAtualizada = await CronogramaViewModel.atualizarAtividadeUsuario(atividade);

            setAtividadesUsuario(prevAtividades =>
                prevAtividades.map(atividadeAntiga =>
                    atividadeAntiga.id === atividadeAtualizada.id ? {
                        id: atividadeAtualizada.id,
                        title: atividadeAtualizada.descricao,
                        start: new Date(`${atividadeAtualizada.data}T${atividadeAtualizada.hora}`),
                        end: new Date(`${atividadeAtualizada.data}T${atividadeAtualizada.hora}`),
                        color: azulUsuario,
                        descricao: atividadeAtualizada.descricao,
                        unidade: atividadeAtualizada.unidade,
                        lugar: atividadeAtualizada.lugar,
                        data: atividadeAtualizada.data,
                        hora: atividadeAtualizada.hora,
                        cronogramaId: atividadeAtualizada.cronogramaId,
                    } : atividadeAntiga
                )
            );
            setShowModal(false);
            setEventoSelecionado(null);
            toast.success('Atividade atualizada com sucesso!');
        } catch (error) {
            toast.error('Erro ao atualizar atividade.');
        }
    };

    const handleDeleteAtividade = async (id: string) => {
        try {
            await CronogramaViewModel.deletarAtividadeUsuario(id);
            setAtividadesUsuario((prevAtividades) => prevAtividades.filter((atividade) => atividade.id !== id));
            toast.success('Atividade excluída!');
        } catch (error) {
            console.error('Erro ao excluir a atividade:', error);
            toast.error('Erro ao excluir a atividade.');
        }
    };

    const toggleLegendas = () => {
        setLegendasVisiveis(!legendasVisiveis);
    };

    return (
        <>
            {showTips && <Tips onClose={handleTipsClose} />}
            <div className="calendar-container" ref={calendarRef} style={{ height: calendarHeight, marginTop: '38px' }}>
                <Calendar
                    localizer={localizer}
                    culture="pt-BR"
                    events={[...atividadesUsuario, ...atividadesOutras]}
                    startAccessor="start"
                    endAccessor="end"
                    style={{ height: '65%', width: '97%' }}
                    view={view}
                    onView={newView => setView(newView)}
                    popup={false}
                    selectable
                    onSelectSlot={handleSelectSlot}
                    onSelectEvent={handleSelectEvent}
                    messages={{
                        next: 'Próximo',
                        previous: 'Anterior',
                        today: 'Hoje',
                        month: 'Mês',
                        week: 'Semana',
                        day: 'Dia',
                        agenda: 'Agenda',
                        showMore: (total) => `+${total} mais`,
                        noEventsInRange: 'Nenhuma atividade neste período.',
                        allDay: 'Dia inteiro',
                        date: 'Data',
                        time: 'Hora',
                        event: 'Evento',
                    }}
                    eventPropGetter={event => ({
                        style: { backgroundColor: event.color }
                    })}
                />
                {legendasVisiveis && (
                    <div className="legendas-flutuantes">
                        <LegendaProcessos
                            atividadesOutrasDTO={atividadesOutrasDTO}
                            corUsuario={azulUsuario}
                        />
                    </div>
                )}
                <div className="toggle-legendas">
                    <button onClick={toggleLegendas}>
                        {legendasVisiveis ? <FaTimesCircle /> : <FaClosedCaptioning />}
                    </button>
                </div>
                <PermissaoWrapper permissoesRequiridas={[(PermissaoEnum.CONCLUIR_CRONOGRAMA)]}>
                    <Button
                        disabled={!(atividadesUsuario.length > 0)}
                        variant={showModalConclusao ? "secondary" : "success"}
                        onClick={handleShowConclusao}
                        style={{ position: 'absolute', bottom: '30px', left: 'calc(95% - 150px)', zIndex: 1000 }}
                    >
                        Concluir Definição
                    </Button>
                </PermissaoWrapper>
            </div>
            <PermissaoWrapper permissoesRequiridas={[(PermissaoEnum.CADASTRAR_ATIVIDADE)]}>
                <ModalCriarAtividade
                    show={showModal && eventoSelecionado === null}
                    onHide={() => setShowModal(false)}
                    onSave={handleCreateAtividade}
                    selectedDate={selectedDate}
                    cronogramaId={processo?.cronograma?.id || 'ERROR: Calendario.tsx: processoUsuario.cronograma.id inválido'}
                />
            </PermissaoWrapper>
            <PermissaoWrapper permissoesRequiridas={[(PermissaoEnum.ATUALIZAR_ATIVIDADE)]}>
                <ModalEditarAtividade
                    show={showModal && eventoSelecionado !== null}
                    onHide={() => {
                        setShowModal(false);
                        setEventoSelecionado(null);
                    }}
                    onDelete={handleDeleteAtividade}
                    onSave={handleEditAtividade}
                    evento={eventoSelecionado}
                    cronogramaId={processo?.cronograma?.id || 'ERROR: Calendario.tsx: processoUsuario.cronograma.id inválido'}
                />
            </PermissaoWrapper>
            <ModalConclusao processoId={processo!.id} cronogramaId={processo!.cronograma!.id} atividadesUsuario={atividadesUsuario} setAtividadesUsuario={setAtividadesUsuario} show={showModalConclusao} handleClose={handleCloseConclusao} />
        </>
    );
};

export default Calendario;